<template>
  <div class="listed-info-panel">
    <transition name="toast-fade">
      <div v-if="toast.show" :class="['toast', 'toast-' + toast.type]" @click="toast.show = false">
        {{ toast.message }}
      </div>
    </transition>

    <div class="panel-toolbar">
      <h3>公司信息</h3>
      <p class="toolbar-hint">全局参考表，用于数据审核 enrich。导入时填写「本批版本」会<strong>仅替换该版本</strong>下的证券代码行，其它版本快照保留；数据审核中可按版本切换 LEFT JOIN。</p>
      <div class="toolbar-actions">
        <label v-if="listedDataVersions.length" class="toolbar-version-wrap">
          <span class="toolbar-version-label">查看版本</span>
          <select v-model="selectedDataVersion" class="toolbar-version-select" @change="onDataVersionChange">
            <option value="">全部</option>
            <option v-for="v in listedDataVersions" :key="v" :value="v">{{ v }}</option>
          </select>
        </label>
        <input
          v-model="searchKeyword"
          type="text"
          class="search-input"
          placeholder="搜索代码、简称、省份、行业…"
          @input="onSearchInput"
          @keyup.enter="onSearchEnter"
        />
        <button type="button" class="btn btn-secondary btn-sm" @click="exportExcel">下载 Excel</button>
        <button type="button" class="btn btn-primary btn-sm" @click="openUploadWizard">上传 Excel</button>
      </div>
    </div>

    <div v-if="listLoading" class="loading-state">加载中…</div>
    <div v-else class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="col in tableColumns" :key="col">
              <div v-if="isFilterColumn(col)" class="th-wrapper">
                {{ columnLabel(col) }}
                <span class="filter-icon" @click.stop="toggleFilterDropdown(col)">⏷</span>
                <div
                  v-show="showFilterDropdown[col]"
                  class="filter-dropdown"
                  :data-dropdown="col"
                  @click.stop
                >
                  <input
                    class="filter-search-input"
                    v-model="filterSearch[col]"
                    placeholder="搜索该列…"
                  />
                  <div class="filter-options">
                    <label v-for="val in getFilteredOptions(col)" :key="String(val)">
                      <input type="checkbox" :value="val" v-model="pendingFilters[col]" />
                      {{ val }}
                    </label>
                  </div>
                  <div class="filter-footer">
                    <button type="button" @click="clearColumnFilter(col)">清空筛选</button>
                    <button type="button" class="btn-filter-ok" @click="confirmFilter(col)">确定</button>
                  </div>
                </div>
              </div>
              <template v-else>{{ columnLabel(col) }}</template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="rows.length === 0">
            <td :colspan="Math.max(tableColumns.length, 1)" class="empty-cell">暂无数据，请上传公司信息 Excel</td>
          </tr>
          <tr v-for="row in rows" :key="row.id">
            <td v-for="col in tableColumns" :key="col">{{ displayCell(row, col) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalRows > 0" class="pagination-bar">
      <span>共 {{ totalRows }} 条</span>
      <button type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
      <span>第 {{ page }} / {{ totalPages }} 页</span>
      <button type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
    </div>

    <transition name="modal-fade">
      <div v-if="wizardVisible" class="modal" @mousedown.self="closeWizard">
        <div class="modal-content modal-wide">
          <h3>上传公司信息</h3>
          <div v-if="wizardStep === 1" class="wizard-step">
            <p class="wizard-step-label">步骤 1 / 2 · 选择文件与批次版本</p>
            <p class="wizard-hint">选择 Excel，确保首行是表头。</p>
            <div class="upload-version-block upload-version-block-step1">
              <label class="upload-version-label">本批数据版本 <span class="req">*</span></label>
              <input
                v-model.trim="uploadDataVersion"
                type="text"
                class="upload-version-input"
                maxlength="32"
                placeholder="如 25Q3、2025Q3"
                autocomplete="off"
              />
              <p class="upload-version-hint">
                整表导入后所有行共用此版本号；在「数据审核」中会显示，便于辨认当前匹配的公司信息快照。
              </p>
            </div>
            <input
              ref="fileInput"
              type="file"
              class="file-input-hidden"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              @change="onFilePick"
            />
            <div
              class="excel-drop-zone"
              :class="{ 'is-dragging': dropActive, 'has-file': !!pendingFile }"
              @click="triggerFileInput"
              @keydown.enter.prevent="triggerFileInput"
              @keydown.space.prevent="triggerFileInput"
              tabindex="0"
              role="button"
              :aria-label="'选择或拖放 Excel 文件'"
              @dragenter.prevent="onDragEnter"
              @dragleave.prevent="onDragLeave"
              @dragover.prevent="onDragOver"
              @drop.prevent="onDrop"
            >
              <div v-if="!pendingFile" class="drop-zone-empty">
                <span class="drop-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                      stroke="currentColor"
                      stroke-width="1.75"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <span class="drop-title">将 Excel 拖到此处，或<span class="drop-link">点击选择文件</span></span>
                <span class="drop-sub">支持 .xlsx、.xls，首行需为表头</span>
              </div>
              <div v-else class="drop-zone-file" @click.stop>
                <span class="file-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                      stroke="currentColor"
                      stroke-width="1.75"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" />
                  </svg>
                </span>
                <div class="file-meta">
                  <span class="file-name">{{ pendingFile.name }}</span>
                  <span class="file-size">{{ formatFileSize(pendingFile.size) }}</span>
                </div>
                <button type="button" class="btn-remove-file" @click.stop="removePendingFile">更换</button>
              </div>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeWizard">取消</button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!pendingFile || parseLoading || !(uploadDataVersion || '').trim()"
                @click="runParse"
              >
                {{ parseLoading ? '解析中…' : '下一步' }}
              </button>
            </div>
          </div>
          <div v-else-if="wizardStep === 2" class="wizard-step">
            <p class="wizard-step-label">步骤 2 / 2 · 列映射与预览</p>
            <p class="wizard-hint wizard-hint-inline">
              本批版本：<strong class="version-inline">{{ (uploadDataVersion || '').trim() || '—' }}</strong>
            </p>
            <p class="map-intro">
              将 Excel 表头映射到<strong>入库字段名</strong>（下表第二列）。<strong>证券代码</strong>
              <code class="inline-code">sec_code</code> 为必选，需与爬虫结果、后续 JOIN 使用的代码格式一致（如 600000 / 000001）。
              自动映射可与本地缓存合并。
            </p>
            <div v-if="parseConflicts.length" class="conflict-banner">{{ parseConflicts.join(' ') }}</div>
            <table class="map-table">
              <thead>
                <tr>
                  <th>系统字段</th>
                  <th class="col-field-key">入库字段名（JOIN）</th>
                  <th>Excel 列</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="f in fieldDefs"
                  :key="f.fieldKey"
                  :class="{
                    'row-error': f.fieldKey === 'sec_code' && (!mapping[f.fieldKey] || mapping[f.fieldKey] === MAP_NONE),
                    'row-join-key': f.fieldKey === 'sec_code'
                  }"
                >
                  <td>
                    {{ f.label }}
                    <span v-if="f.fieldKey === 'sec_code'" class="req">*</span>
                    <span v-if="f.fieldKey === 'sec_code'" class="tag-join">关联键</span>
                  </td>
                  <td class="field-key-cell">
                    <code class="field-key-code">{{ f.fieldKey }}</code>
                  </td>
                  <td>
                    <select v-model="mapping[f.fieldKey]" class="map-select">
                      <option :value="MAP_NONE">不导入该列</option>
                      <option v-for="h in excelHeaders" :key="h" :value="h">{{ h }}</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="mapping-preview-block">
              <h4 class="mapping-preview-title">
                <span class="badge-join">预览</span>
                映射结果预览（前 {{ previewRowCount }} 行 Excel，与入库逻辑一致）
              </h4>
              <p v-if="!mapping.sec_code || mapping.sec_code === MAP_NONE" class="preview-placeholder">
                请先为 <strong>证券代码</strong> 选择对应的 Excel 列，即可查看将写入数据库的样例。
              </p>
              <template v-else>
                <p class="preview-hint">
                  下表为根据当前映射从 Excel 解析出的样例；确认
                  <code class="inline-code">sec_code</code>
                  等列内容正确后再点「开始导入」。
                </p>
                <div v-if="previewMappedRows.length" class="preview-table-wrap">
                  <table class="preview-table">
                    <thead>
                      <tr>
                        <th v-for="col in previewDisplayColumns" :key="col.key" :class="{ 'th-join': col.key === 'sec_code' }">
                          <span>{{ col.label }}</span>
                          <code class="th-key">{{ col.dbKey || col.key }}</code>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(pr, idx) in previewMappedRows" :key="idx">
                        <td v-for="col in previewDisplayColumns" :key="col.key" :class="{ 'td-join': col.key === 'sec_code' }">
                          {{ displayPreviewCell(pr, col.key) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="preview-placeholder">
                  当前预览行中暂无有效证券代码，请检查 Excel 首列数据或映射是否正确。
                </p>
              </template>
            </div>

            <div class="extra-section" v-if="unmappedHeaders.length">
              <h4>其他字段（可选）</h4>
              <label v-for="h in unmappedHeaders" :key="h" class="extra-check">
                <input type="checkbox" :value="h" v-model="extraSelected" />
                {{ h }}
              </label>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="wizardStep = 1">上一步</button>
              <button type="button" class="btn btn-secondary" @click="clearMapping">清空映射</button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!mapping.sec_code || importLoading"
                @click="submitImport"
              >
                {{ importLoading ? '导入中…' : '开始导入' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { createApiClient } from '@/services/httpSetup';
import { toastMixin } from '@/utils/toastMixin.js';

const api = createApiClient({ baseURL: '/api/crawler' });
const LS_KEY = 'listed_info_column_map_v1';
const LS_DATA_VERSION_FILTER = 'crawlerCompanyInfoDataVersion';
const MAP_NONE = '__none__';

const FIXED_COLS = [
  { key: 'sec_code', label: '证券代码' },
  { key: 'sec_name', label: '公司/简称' },
  { key: 'province', label: '省份' },
  { key: 'industry', label: '行业' },
  { key: 'controller_type', label: '实控人' },
  { key: 'market_value', label: '市值(亿元)' },
  { key: 'market_scale', label: '市值规模' },
  { key: 'asset_ratio', label: '资产占比' },
  { key: 'data_version', label: '参考表版本' },
  { key: 'updated_at', label: '更新时间' }
];

export default {
  name: 'CrawlerCompanyInfoPanel',
  mixins: [toastMixin],
  data() {
    return {
      MAP_NONE,
      rows: [],
      totalRows: 0,
      page: 1,
      pageSize: 20,
      listLoading: false,
      searchKeyword: '',
      searchDebounceTimer: null,
      filterOptions: { provinces: [], industries: [] },
      activeColumnFilters: { province: [], industry: [] },
      showFilterDropdown: {},
      pendingFilters: { province: [], industry: [] },
      filterSearch: { province: '', industry: '' },
      extraKeys: [],
      wizardVisible: false,
      wizardStep: 1,
      pendingFile: null,
      dropActive: false,
      dragCounter: 0,
      parseLoading: false,
      importLoading: false,
      excelHeaders: [],
      previewRows: [],
      suggestedMapping: {},
      parseConflicts: [],
      fieldDefs: [],
      mapping: {},
      extraSelected: [],
      /** 整表导入时必填，写入库内每行 data_version */
      uploadDataVersion: '',
      listedDataVersions: [],
      /** 列表筛选用：空=全部版本行 */
      selectedDataVersion: ''
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.pageSize));
    },
    tableColumns() {
      const base = FIXED_COLS.map((c) => c.key);
      return [...base, ...this.extraKeys];
    },
    unmappedHeaders() {
      const used = new Set(
        Object.values(this.mapping || {}).filter((v) => v && v !== MAP_NONE)
      );
      return (this.excelHeaders || []).filter((h) => !used.has(h));
    },
    previewRowCount() {
      return (this.previewRows || []).length;
    },
    previewMappedRows() {
      const rows = this.previewRows || [];
      return rows.map((row) => this.mapRowPreview(row)).filter((r) => r != null);
    },
    previewDisplayColumns() {
      const cols = [];
      const defs = this.fieldDefs || [];
      for (const f of defs) {
        const m = this.mapping[f.fieldKey];
        if (m && m !== MAP_NONE) {
          cols.push({ key: f.fieldKey, label: f.label, dbKey: f.fieldKey });
        }
      }
      if ((this.extraSelected || []).length) {
        cols.push({ key: '__extra__', label: '扩展(JSON)', dbKey: 'extra' });
      }
      return cols;
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.handleFilterClickOutside);
    try {
      const s = localStorage.getItem(LS_DATA_VERSION_FILTER);
      if (s) this.selectedDataVersion = s;
    } catch (e) {
      /* ignore */
    }
    this.loadListedDataVersions().then(() => {
      this.loadFilterOptions();
      this.loadList();
    });
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleFilterClickOutside);
    clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    isFilterColumn(col) {
      return col === 'province' || col === 'industry';
    },
    optionsForFilterColumn(col) {
      if (col === 'province') return this.filterOptions.provinces || [];
      if (col === 'industry') return this.filterOptions.industries || [];
      return [];
    },
    getFilteredOptions(col) {
      const options = this.optionsForFilterColumn(col);
      const search = (this.filterSearch[col] || '').toLowerCase();
      if (!search) return options;
      return options.filter((val) => String(val).toLowerCase().includes(search));
    },
    toggleFilterDropdown(col) {
      if (!this.isFilterColumn(col)) return;
      const currentVisibility = this.showFilterDropdown[col];
      this.closeAllDropdowns();
      this.showFilterDropdown = { ...this.showFilterDropdown, [col]: !currentVisibility };
      if (!currentVisibility) {
        const current = this.activeColumnFilters[col] || [];
        this.pendingFilters = { ...this.pendingFilters, [col]: [...current] };
        this.filterSearch = { ...this.filterSearch, [col]: '' };
      }
    },
    confirmFilter(col) {
      const values = this.pendingFilters[col] || [];
      this.activeColumnFilters = { ...this.activeColumnFilters, [col]: [...values] };
      this.closeAllDropdowns();
      this.page = 1;
      this.loadList();
    },
    clearColumnFilter(col) {
      this.pendingFilters = { ...this.pendingFilters, [col]: [] };
      this.activeColumnFilters = { ...this.activeColumnFilters, [col]: [] };
      this.closeAllDropdowns();
      this.page = 1;
      this.loadList();
    },
    closeAllDropdowns() {
      this.showFilterDropdown = {};
    },
    handleFilterClickOutside(event) {
      const isInside = event.target.closest('.filter-dropdown, .filter-icon');
      if (!isInside) this.closeAllDropdowns();
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1;
        this.loadList();
      }, 500);
    },
    onSearchEnter() {
      clearTimeout(this.searchDebounceTimer);
      this.page = 1;
      this.loadList();
    },
    onDataVersionChange() {
      try {
        if (this.selectedDataVersion) {
          localStorage.setItem(LS_DATA_VERSION_FILTER, this.selectedDataVersion);
        } else {
          localStorage.removeItem(LS_DATA_VERSION_FILTER);
        }
      } catch (e) {
        /* ignore */
      }
      this.page = 1;
      this.loadFilterOptions();
      this.loadList();
    },
    async loadListedDataVersions() {
      try {
        const { data } = await api.get('/listed-information/data-versions');
        this.listedDataVersions = data.versions || [];
        if (
          this.selectedDataVersion &&
          !this.listedDataVersions.includes(this.selectedDataVersion)
        ) {
          this.selectedDataVersion = '';
          try {
            localStorage.removeItem(LS_DATA_VERSION_FILTER);
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        this.listedDataVersions = [];
      }
    },
    async loadFilterOptions() {
      try {
        const params = {};
        if (this.selectedDataVersion) params.dataVersion = this.selectedDataVersion;
        const { data } = await api.get('/listed-information/filter-options', { params });
        this.filterOptions = {
          provinces: data.provinces || [],
          industries: data.industries || []
        };
      } catch (e) {
        this.showToast('筛选项加载失败: ' + (e.response?.data?.error || e.message), 'error');
      }
    },
    columnLabel(col) {
      const f = FIXED_COLS.find((c) => c.key === col);
      if (f) return f.label;
      return col;
    },
    /**
     * 与后端 buildMappedListedRows 一致，用于步骤 2 预览
     */
    mapRowPreview(row) {
      const m = this.mapping;
      const pick = (fk) => {
        const h = m[fk];
        if (!h || h === MAP_NONE) return undefined;
        const v = row[h];
        if (v === '' || v === undefined || v === null) return undefined;
        return v;
      };
      const secRaw = pick('sec_code');
      if (secRaw === undefined) return null;
      const sec_code = String(secRaw).trim();
      if (!sec_code) return null;

      const o = {
        sec_code,
        sec_name: pick('sec_name'),
        province: pick('province'),
        industry: pick('industry'),
        controller_type: pick('controller_type')
      };
      const mv = pick('market_value');
      if (mv !== undefined) {
        const n = typeof mv === 'number' ? mv : parseFloat(String(mv).replace(/,/g, ''));
        if (Number.isFinite(n)) o.market_value = n;
      }
      const ar = pick('asset_ratio');
      if (ar !== undefined) {
        const n = typeof ar === 'number' ? ar : parseFloat(String(ar).replace(/,/g, ''));
        if (Number.isFinite(n)) o.asset_ratio = n;
      }
      const extra = {};
      for (const eh of this.extraSelected || []) {
        if (!eh || eh === MAP_NONE) continue;
        const v = row[eh];
        if (v !== '' && v !== undefined && v !== null) {
          extra[eh] = typeof v === 'number' ? v : String(v);
        }
      }
      if (Object.keys(extra).length) o.extra = extra;
      return o;
    },
    displayPreviewCell(pr, colKey) {
      if (colKey === '__extra__') {
        if (!pr.extra || !Object.keys(pr.extra).length) return '—';
        try {
          return JSON.stringify(pr.extra);
        } catch (e) {
          return String(pr.extra);
        }
      }
      const v = pr[colKey];
      if (v === undefined || v === null || v === '') return '—';
      if (typeof v === 'number') {
        if (colKey === 'market_value' || colKey === 'asset_ratio') return String(v);
        return String(v);
      }
      return String(v);
    },
    displayCell(row, col) {
      if (col === 'extra') return '';
      if (row.extra && Object.prototype.hasOwnProperty.call(row.extra, col)) {
        const v = row.extra[col];
        return v === null || v === undefined ? '' : String(v);
      }
      const v = row[col];
      if (v === null || v === undefined) {
        return col === 'data_version' ? '—' : '';
      }
      if (col === 'data_version' && String(v).trim() === '') return '—';
      if (col === 'updated_at' && v) {
        try {
          return new Date(v).toLocaleString('zh-CN');
        } catch (e) {
          return String(v);
        }
      }
      return String(v);
    },
    collectExtraKeysFromRows(list) {
      const s = new Set();
      (list || []).forEach((row) => {
        if (row.extra && typeof row.extra === 'object') {
          Object.keys(row.extra).forEach((k) => s.add(k));
        }
      });
      this.extraKeys = [...s].sort();
    },
    async loadList() {
      this.listLoading = true;
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize
        };
        if (this.searchKeyword) params.searchKeyword = this.searchKeyword;
        if (this.selectedDataVersion) params.dataVersion = this.selectedDataVersion;
        const pf = this.activeColumnFilters.province;
        const ind = this.activeColumnFilters.industry;
        if (pf && pf.length) params.provinces = JSON.stringify(pf);
        if (ind && ind.length) params.industries = JSON.stringify(ind);
        const { data } = await api.get('/listed-information', { params });
        this.rows = data.data || [];
        this.totalRows = data.total || 0;
        this.collectExtraKeysFromRows(this.rows);
      } catch (e) {
        this.showToast('加载失败: ' + (e.response?.data?.error || e.message), 'error');
      } finally {
        this.listLoading = false;
      }
    },
    changePage(p) {
      this.page = p;
      this.loadList();
    },
    exportExcel() {
      const q = this.selectedDataVersion
        ? `?dataVersion=${encodeURIComponent(this.selectedDataVersion)}`
        : '';
      window.open(`/api/crawler/listed-information/export${q}`, '_blank');
    },
    openUploadWizard() {
      this.wizardVisible = true;
      this.wizardStep = 1;
      this.uploadDataVersion = '';
      this.pendingFile = null;
      this.dropActive = false;
      this.dragCounter = 0;
      this.excelHeaders = [];
      this.mapping = {};
      this.extraSelected = [];
      this.parseConflicts = [];
      this.previewRows = [];
      this.$nextTick(() => {
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      });
    },
    closeWizard() {
      this.wizardVisible = false;
    },
    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onDragEnter() {
      this.dragCounter++;
      this.dropActive = true;
    },
    onDragLeave() {
      this.dragCounter--;
      if (this.dragCounter <= 0) {
        this.dragCounter = 0;
        this.dropActive = false;
      }
    },
    onDragOver(e) {
      e.preventDefault();
    },
    onDrop(e) {
      this.dragCounter = 0;
      this.dropActive = false;
      const file = e.dataTransfer?.files?.[0];
      if (file) this.assignExcelFile(file);
    },
    assignExcelFile(file) {
      const name = file.name || '';
      if (!/\.(xlsx|xls)$/i.test(name)) {
        this.showToast('请选择 .xlsx 或 .xls 文件', 'error');
        return;
      }
      this.pendingFile = file;
    },
    removePendingFile() {
      this.pendingFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      this.$nextTick(() => this.triggerFileInput());
    },
    formatFileSize(bytes) {
      if (bytes == null || Number.isNaN(bytes)) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    onFilePick(e) {
      const file = e.target.files?.[0];
      if (file) this.assignExcelFile(file);
      else this.pendingFile = null;
    },
    mergeCacheAndSuggested(suggested) {
      let cache = {};
      try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) cache = JSON.parse(raw);
      } catch (err) {
        cache = {};
      }
      const out = {};
      (this.fieldDefs || []).forEach((f) => {
        const k = f.fieldKey;
        if (suggested[k]) out[k] = suggested[k];
        else if (cache.mapping && cache.mapping[k] && cache.mapping[k] !== MAP_NONE) {
          out[k] = cache.mapping[k];
        } else {
          out[k] = MAP_NONE;
        }
      });
      return out;
    },
    async runParse() {
      if (!this.pendingFile) return;
      if (!(this.uploadDataVersion || '').trim()) {
        this.showToast('请先填写本批数据版本（如 25Q3）', 'error');
        return;
      }
      this.parseLoading = true;
      try {
        const fd = new FormData();
        fd.append('file', this.pendingFile);
        const { data } = await api.post('/listed-information/parse-upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        this.fieldDefs = data.fields || [];
        this.excelHeaders = data.headers || [];
        this.previewRows = data.previewRows || [];
        this.suggestedMapping = data.suggestedMapping || {};
        this.parseConflicts = data.conflicts || [];
        this.mapping = this.mergeCacheAndSuggested(this.suggestedMapping);
        let cacheExtra = [];
        try {
          const raw = localStorage.getItem(LS_KEY);
          if (raw) {
            const c = JSON.parse(raw);
            if (Array.isArray(c.extraHeaders)) cacheExtra = c.extraHeaders;
          }
        } catch (err) { /* ignore */ }
        const used = new Set(
          Object.values(this.mapping).filter((v) => v && v !== MAP_NONE)
        );
        const unmapped = this.excelHeaders.filter((h) => !used.has(h));
        this.extraSelected = cacheExtra.filter((h) => unmapped.includes(h));
        this.wizardStep = 2;
      } catch (e) {
        this.showToast(e.response?.data?.error || e.message || '解析失败', 'error');
      } finally {
        this.parseLoading = false;
      }
    },
    clearMapping() {
      const keys = (this.fieldDefs || []).map((f) => f.fieldKey);
      keys.forEach((k) => {
        this.mapping[k] = MAP_NONE;
      });
      this.extraSelected = [];
    },
    saveCache() {
      try {
        localStorage.setItem(
          LS_KEY,
          JSON.stringify({ mapping: { ...this.mapping }, extraHeaders: [...this.extraSelected] })
        );
      } catch (err) { /* ignore */ }
    },
    async submitImport() {
      if (!this.mapping.sec_code || this.mapping.sec_code === MAP_NONE) {
        this.showToast('请选择证券代码对应的 Excel 列', 'error');
        return;
      }
      if (!this.pendingFile) {
        this.showToast('文件已失效，请重新选择', 'error');
        return;
      }
      const ver = (this.uploadDataVersion || '').trim();
      if (!ver) {
        this.showToast('请填写本批数据版本（如 25Q3）', 'error');
        return;
      }
      this.importLoading = true;
      try {
        const fd = new FormData();
        fd.append('file', this.pendingFile);
        fd.append('mapping', JSON.stringify(this.mapping));
        fd.append('dataVersion', ver);
        const allowedExtra = this.extraSelected.filter((h) => this.unmappedHeaders.includes(h));
        fd.append('extraHeaders', JSON.stringify(allowedExtra));
        const { data } = await api.post('/listed-information/import-mapped', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        this.saveCache();
        this.showToast(
          `导入成功，共 ${data.insertedCount} 条；已回写爬虫结果 ${data.applyBackfill?.updatedCount ?? 0} 行`,
          'success'
        );
        this.closeWizard();
        this.page = 1;
        await this.loadListedDataVersions();
        await this.loadFilterOptions();
        await this.loadList();
      } catch (e) {
        this.showToast(e.response?.data?.error || e.message || '导入失败', 'error');
      } finally {
        this.importLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.listed-info-panel {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.panel-toolbar h3 {
  margin: 0 0 8px;
  color: #2e3968;
}
.toolbar-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #666;
}
.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.toolbar-version-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
}
.toolbar-version-label {
  white-space: nowrap;
}
.toolbar-version-select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 120px;
  font-size: 13px;
  background: #fff;
}
.search-input {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 200px;
}
.table-wrap {
  overflow: auto;
  margin-top: 16px;
  max-height: min(92vh, calc(100vh - 280px));
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  -webkit-overflow-scrolling: touch;
}
.data-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
}
.data-table th,
.data-table td {
  border: 1px solid #e0e0e0;
  padding: 8px 10px;
  text-align: left;
}
.data-table th {
  background: #f5f7fa;
  color: #2e3968;
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: 0 1px 0 #e0e0e0;
}
.th-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: visible;
  z-index: 1;
  min-width: 0;
}
.filter-icon {
  flex-shrink: 0;
  margin-left: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #2e3968;
  user-select: none;
}
.filter-dropdown {
  position: absolute;
  z-index: 120;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #ccc;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  width: 220px;
  max-height: 300px;
  display: flex;
  flex-direction: column;
}
.filter-search-input {
  padding: 6px 10px;
  margin: 8px;
  font-size: 13px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.filter-options {
  flex: 1;
  overflow-y: auto;
  padding: 0 10px 10px;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.filter-options label {
  display: flex;
  align-items: center;
  gap: 5px;
}
.filter-options input[type='checkbox'] {
  margin-right: 2px;
}
.filter-footer {
  position: sticky;
  bottom: 0;
  background: #f9f9f9;
  border-top: 1px solid #ddd;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.filter-footer button {
  background: #eef1f6;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #2e3968;
}
.filter-footer button:hover {
  background: #e4e8f0;
}
.btn-filter-ok {
  background: #2e3968 !important;
  color: #fff !important;
}
.btn-filter-ok:hover {
  background: #1e2a52 !important;
}
.empty-cell {
  text-align: center;
  color: #909399;
  padding: 32px;
}
.loading-state {
  padding: 24px;
  text-align: center;
  color: #909399;
}
.pagination-bar {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
}
.btn {
  padding: 6px 14px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 13px;
}
.btn-sm {
  padding: 5px 12px;
  font-size: 12px;
}
.btn-primary {
  background: #2e3968;
  color: #fff;
}
.btn-secondary {
  background: #eef1f6;
  color: #2e3968;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  max-width: 520px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
}
.modal-wide {
  max-width: 880px;
}
.modal-content h3 {
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: #2e3968;
}
.wizard-hint {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: #606266;
}
.wizard-step-label {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #909399;
  text-transform: none;
}
.wizard-step {
  min-height: 120px;
}
.file-input-hidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}
.excel-drop-zone {
  position: relative;
  border: 2px dashed #c0c4cc;
  border-radius: 12px;
  background: linear-gradient(180deg, #fafbfd 0%, #f5f7fa 100%);
  min-height: 168px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
}
.excel-drop-zone:hover {
  border-color: #8b9dc3;
  background: #f0f3f9;
  box-shadow: 0 2px 12px rgba(46, 57, 104, 0.08);
}
.excel-drop-zone:focus {
  outline: none;
}
.excel-drop-zone:focus-visible {
  outline: 2px solid #2e3968;
  outline-offset: 2px;
}
.excel-drop-zone.is-dragging {
  border-color: #2e3968;
  background: #eef2fb;
  box-shadow: 0 0 0 4px rgba(46, 57, 104, 0.12);
}
.excel-drop-zone.has-file {
  min-height: auto;
  padding: 16px 20px;
  cursor: default;
}
.excel-drop-zone.has-file:hover {
  box-shadow: none;
  border-color: #c0c4cc;
  background: linear-gradient(180deg, #fafbfd 0%, #f5f7fa 100%);
}
.drop-zone-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 20px;
  text-align: center;
  pointer-events: none;
}
.drop-zone-empty .drop-icon {
  color: #8b9dc3;
  margin-bottom: 12px;
}
.drop-title {
  font-size: 15px;
  color: #303133;
  line-height: 1.5;
}
.drop-link {
  color: #2e3968;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.drop-sub {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}
.drop-zone-file {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
}
.drop-zone-file .file-icon {
  flex-shrink: 0;
  color: #2e3968;
  opacity: 0.9;
  pointer-events: none;
}
.file-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  pointer-events: none;
}
.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-size {
  font-size: 12px;
  color: #909399;
}
.btn-remove-file {
  flex-shrink: 0;
  padding: 6px 14px;
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #2e3968;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
}
.btn-remove-file:hover {
  background: #f5f7fa;
  border-color: #c0c4cc;
}
.map-table {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 13px;
}
.map-table th,
.map-table td {
  border: 1px solid #e0e0e0;
  padding: 8px;
}
.map-table select,
.map-select {
  width: 100%;
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  font-size: 13px;
  background: #fff;
}
.row-error {
  background: #fff4f4;
}
.row-join-key {
  background: #f8fafc;
}
.field-key-cell {
  vertical-align: middle;
  white-space: nowrap;
}
.col-field-key {
  min-width: 120px;
}
.field-key-code {
  font-size: 12px;
  padding: 2px 6px;
  background: #f0f2f5;
  border-radius: 4px;
  color: #2e3968;
}
.tag-join {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #2e3968;
  background: #e8ecf5;
  border-radius: 3px;
  vertical-align: middle;
}
.inline-code {
  font-size: 12px;
  padding: 1px 5px;
  background: #f0f2f5;
  border-radius: 3px;
}
.mapping-preview-block {
  margin: 20px 0 8px;
  padding: 14px 16px;
  background: #fafbfd;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}
.mapping-preview-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #2e3968;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.badge-join {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
  background: #2e3968;
  border-radius: 4px;
}
.preview-hint {
  margin: 0 0 10px;
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
}
.preview-placeholder {
  margin: 0;
  font-size: 13px;
  color: #909399;
}
.preview-table-wrap {
  overflow-x: auto;
  margin-top: 4px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: #fff;
}
.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.preview-table th,
.preview-table td {
  border: 1px solid #ebeef5;
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
  max-width: 220px;
  word-break: break-word;
}
.preview-table th {
  background: #f5f7fa;
  color: #303133;
}
.preview-table th .th-key {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #909399;
}
.preview-table .th-join {
  background: #eef2fb;
  border-color: #c5cde0;
}
.preview-table .td-join {
  font-weight: 600;
  color: #2e3968;
  background: #fafbfd;
}
.req {
  color: #f56c6c;
}
.upload-version-block {
  margin-bottom: 14px;
  padding: 12px 14px;
  background: #fafbfd;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}
.upload-version-block-step1 {
  margin-bottom: 18px;
  padding: 14px 16px;
  background: #f4f7fc;
  border: 2px solid #c5cde0;
  box-shadow: 0 2px 8px rgba(46, 57, 104, 0.08);
}
.upload-version-block-step1 .upload-version-label {
  font-size: 14px;
}
.upload-version-block-step1 .upload-version-input {
  max-width: 320px;
  font-size: 15px;
  border-color: #b8c0d8;
}
.wizard-hint-inline {
  margin: 0 0 14px;
  font-size: 13px;
  color: #606266;
}
.version-inline {
  color: #2e3968;
  font-size: 14px;
  letter-spacing: 0.03em;
}
.upload-version-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #2e3968;
  margin-bottom: 6px;
}
.upload-version-input {
  width: 100%;
  max-width: 280px;
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
.upload-version-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.map-intro {
  font-size: 13px;
  color: #555;
  line-height: 1.65;
  margin-bottom: 12px;
}
.conflict-banner {
  background: #fff7e6;
  border: 1px solid #fa8c16;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 13px;
}
.extra-section {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}
.extra-section h4 {
  margin: 0 0 8px;
  font-size: 14px;
}
.extra-check {
  display: block;
  margin: 4px 0;
  font-size: 13px;
}
.modal-actions {
  margin-top: 20px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  padding: 10px 20px;
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
}
.toast-success {
  background: #67c23a;
}
.toast-error {
  background: #f56c6c;
}
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.25s;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
